/**
 * DebugFCT — Unified Debug Floating Combat Text API.
 *
 * One-liner calls from any system. Checks per-channel toggle automatically.
 * All messages are stored in a 100-entry history buffer regardless of toggle state.
 *
 * Usage:
 *   DebugFCT.successPlayer('cast', 'Frostbolt -30mp CD4s')
 *   DebugFCT.fail('vfx', 'Unknown: bad_effect', x, y)
 *   DebugFCT.info('network', '← npcDamaged', x, y)
 *
 * History:
 *   DebugFCT.getHistory()   → array of recent entries
 *   DebugFCT.copyHistory()  → formatted text for clipboard
 */

import { DevConfig } from '../../config/environment/devConfig';
import { ServiceRegistry } from '../core/ServiceRegistry';

export type FCTChannel = 'cast' | 'vfx' | 'combat' | 'network';
export type FCTLevel = 'success' | 'fail' | 'info';

export interface DebugFCTEntry {
  /** Monotonically-increasing per-record id. Stable across the entry's lifetime
   *  so consumers (CombatLogPanel) can use it as a React key — index-based keys
   *  break when the bounded history shifts on overflow. */
  id: number;
  time: number;
  channel: FCTChannel;
  level: FCTLevel;
  text: string;
}

const CHANNEL_PREFIX: Record<FCTChannel, string> = {
  cast: '',
  vfx: '[VFX] ',
  combat: '[DMG] ',
  network: '[NET] ',
};

const CHANNEL_LABEL: Record<FCTChannel, string> = {
  cast: 'CAST',
  vfx: 'VFX',
  combat: 'DMG',
  network: 'NET',
};

const COLORS = {
  success: '#51cf66',
  fail: '#ff6b6b',
  info: '#74c0fc',
} as const;

const MAX_HISTORY = 100;

// Listeners for the CombatLogPanel (React state sync)
type HistoryListener = (entry: DebugFCTEntry) => void;

class DebugFCTImpl {
  private lastSpawnTime = 0;
  private yOffset = 0;
  private history: DebugFCTEntry[] = [];
  private listeners: Set<HistoryListener> = new Set();

  // ── Public API ─────────────────────────────────────────────

  success(channel: FCTChannel, text: string, x: number, y: number): void {
    this.record(channel, 'success', text);
    if (!this.isEnabled(channel)) return;
    this.spawn(CHANNEL_PREFIX[channel] + text, COLORS.success, x, y);
  }

  fail(channel: FCTChannel, text: string, x: number, y: number): void {
    this.record(channel, 'fail', text);
    if (!this.isEnabled(channel)) return;
    this.spawn(CHANNEL_PREFIX[channel] + '✗ ' + text, COLORS.fail, x, y);
  }

  info(channel: FCTChannel, text: string, x: number, y: number): void {
    this.record(channel, 'info', text);
    if (!this.isEnabled(channel)) return;
    this.spawn(CHANNEL_PREFIX[channel] + text, COLORS.info, x, y);
  }

  successPlayer(channel: FCTChannel, text: string): void {
    this.record(channel, 'success', text);
    if (!this.isEnabled(channel)) return;
    const pos = this.getPlayerPos();
    if (pos) this.spawn(CHANNEL_PREFIX[channel] + text, COLORS.success, pos.x, pos.y);
  }

  failPlayer(channel: FCTChannel, text: string): void {
    this.record(channel, 'fail', text);
    if (!this.isEnabled(channel)) return;
    const pos = this.getPlayerPos();
    if (pos) this.spawn(CHANNEL_PREFIX[channel] + '✗ ' + text, COLORS.fail, pos.x, pos.y);
  }

  infoPlayer(channel: FCTChannel, text: string): void {
    this.record(channel, 'info', text);
    if (!this.isEnabled(channel)) return;
    const pos = this.getPlayerPos();
    if (pos) this.spawn(CHANNEL_PREFIX[channel] + text, COLORS.info, pos.x, pos.y);
  }

  // ── Structured Cast Pipeline API ────────────────────────────
  //
  // castStage emits a connected per-ability narrative across the cast
  // pipeline. Each stage gets a consistent prefix so a reader can scan
  // the CombatLogPanel and follow ONE cast end-to-end:
  //
  //   [CAST] · fireball:intent — target=Bot Warrior 1
  //   [CAST] ✓ fireball:gate-mana — -20mp (80mp left)
  //   [CAST] ✓ fireball:execute — CD5s, gcd500ms
  //   [VFX]  ✓ fireball:vfx-applied — fire_burst
  //   [CAST] · fireball:projectile-spawn — id=p_001 → Bot Warrior 1
  //   [CAST] ✓ fireball:projectile-hit — Bot Warrior 1 (dist=120px t=234ms)
  //   [DMG]  ✓ fireball:damage-applied [SRV] — 34 dmg → Bot Warrior 1 (HP 100→66)
  //
  // Routes through the same channel toggles as other DebugFCT calls so
  // per-channel mute still works. Caller picks the channel that fits the
  // stage (default cast for lifecycle, vfx for vfx-*, combat for damage-*).
  //
  // Stage strings are FREE-FORM (not enum) so callers can use any verb
  // that makes sense per ability — but follow the convention `<verb>` or
  // `<verb>-<modifier>` for greppability.

  castStage(
    abilityId: string,
    stage: string,
    status: FCTLevel,
    detail?: string,
    channel: FCTChannel = 'cast',
  ): void {
    const text = detail
      ? `${abilityId}:${stage} — ${detail}`
      : `${abilityId}:${stage}`;
    this.record(channel, status, text);
    if (!this.isEnabled(channel)) return;
    const pos = this.getPlayerPos();
    if (pos) {
      const sym = status === 'fail' ? '✗ ' : status === 'success' ? '✓ ' : '· ';
      this.spawn(CHANNEL_PREFIX[channel] + sym + text, COLORS[status], pos.x, pos.y);
    }
  }

  // ── History API ─────────────────────────────────────────────

  getHistory(): DebugFCTEntry[] {
    return [...this.history];
  }

  copyHistory(count = 50): string {
    const entries = this.history.slice(-count);
    return entries.map(e => {
      const t = new Date(e.time).toLocaleTimeString('en-US', { hour12: false });
      const lvl = e.level === 'fail' ? '✗' : e.level === 'success' ? '✓' : '·';
      return `[${t}] [${CHANNEL_LABEL[e.channel]}] ${lvl} ${e.text}`;
    }).join('\n');
  }

  /** Subscribe to new entries (for CombatLogPanel React state sync) */
  subscribe(listener: HistoryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ── Internals ──────────────────────────────────────────────

  private nextId = 0;

  private record(channel: FCTChannel, level: FCTLevel, text: string): void {
    const entry: DebugFCTEntry = { id: ++this.nextId, time: Date.now(), channel, level, text };
    this.history.push(entry);
    if (this.history.length > MAX_HISTORY) this.history.shift();
    for (const listener of this.listeners) {
      try { listener(entry); } catch { /* never crash */ }
    }
  }

  private isEnabled(channel: FCTChannel): boolean {
    const cfg = (DevConfig as any).debugFCT;
    return cfg?.[channel] ?? false;
  }

  private getPlayerPos(): { x: number; y: number } | null {
    try {
      const registry = ServiceRegistry.getInstance();
      if (!registry.has('EntitySystem')) return null;
      const entitySystem = registry.get('EntitySystem');
      const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
      if (!player?.position) return null;
      return { x: player.position.x, y: player.position.y - 50 };
    } catch {
      return null;
    }
  }

  private spawn(text: string, color: string, x: number, y: number): void {
    const now = Date.now();
    if (now - this.lastSpawnTime < 200) {
      this.yOffset += 18;
    } else {
      this.yOffset = 0;
    }
    this.lastSpawnTime = now;

    try {
      const registry = ServiceRegistry.getInstance();
      if (!registry.has('EntitySystem')) return;
      // VFXSystem owns showFloatingText (it's a registered service, not an
      // EntitySystem accessor — the old `entitySystem.getVFXSystem()` was a
      // silently-dead path the typed registry surfaced). This whole method only
      // runs when a DevConfig.debugFCT channel is enabled (isEnabled gate), so
      // routing it to the real service is debug-gated, not a gameplay change.
      const vfxSystem = registry.get('VFXSystem', { optional: true });
      if (!vfxSystem?.showFloatingText) return;

      vfxSystem.showFloatingText(text, x, y - this.yOffset, {
        text,
        color,
        fontSize: '13px',
        duration: 1500,
        offsetY: 0,
        fade: true,
      });
    } catch {
      // Debug tool — never crash the game
    }
  }
}

export const DebugFCT = new DebugFCTImpl();
